import { IMAGES } from "./marketingData";

// SEO blog / insights — real long-form articles targeting long-tail search.
export const POSTS = [
  {
    slug: "moving-odc-cargo-to-leh",
    title: "Moving ODC Cargo to Leh: What It Really Takes",
    tag: "ODC",
    date: "2026-06-12",
    readTime: "6 min read",
    image: IMAGES.lehConvoy,
    excerpt:
      "Delivering over-dimensional cargo to Leh and Ladakh means beating altitude, weather and some of the hardest roads in India. Here's how it's done, and done safely.",
    description:
      "A practical guide to moving ODC and heavy cargo to Leh and Ladakh — altitude, weather windows, permits, route surveys and convoy planning for the Himalayan border regions.",
    keywords: "Leh Ladakh cargo transport, ODC transport to Leh, high altitude transport India, border region logistics",
    body: [
      { h: "Why Leh is a different kind of movement", p: [
        "Most over-dimensional movements are a question of weight, height and road width. A movement to Leh adds three more: altitude, temperature and a very short weather window. Passes on the Manali–Leh and Srinagar–Leh axes climb above 5,000 metres, where engines lose power, hydraulics slow down and crews tire quickly.",
        "That is why a Leh movement is planned months before the first wheel turns — around the few months a year the passes are reliably open.",
      ]},
      { h: "It starts with a route survey", p: [
        "Before we commit a single trailer, our team surveys the actual road — bridge load limits, hairpin radii, tunnel heights and the state of the surface after the winter. For an over-dimensional load, one blind hairpin or a low army bridge can end a movement, so nothing is left to a map.",
      ]},
      { h: "Permits, escorts and convoy discipline", p: [
        "Border-region movements need state permits and, for defence consignments, coordination with the authorities and army escorts. We build the load into a disciplined convoy — pilot vehicles ahead, spacing maintained, night halts planned at safe altitudes to let crews acclimatise.",
      ]},
      { h: "The ITO difference", p: [
        "We have been moving India's heaviest cargo since 1972, including into the highest border regions. That experience — knowing which pass to take, when to move and how to keep a load stable at altitude — is what gets construction machinery, transformers and defence equipment to Leh intact and on time.",
      ]},
    ],
  },
  {
    slug: "air-suspension-vs-standard-trailers",
    title: "Air-Suspension vs Standard Trailers: Which Does Your Machinery Need?",
    tag: "Fleet",
    date: "2026-06-05",
    readTime: "5 min read",
    image: IMAGES.airSuspension,
    excerpt:
      "Not every load needs an air-suspension trailer — but some absolutely do. Here's how to tell, and why the wrong choice can cost you a calibration.",
    description:
      "Air-suspension trailers vs standard leaf-spring trailers for machinery transport in India — when road shock matters, and how air suspension protects sensitive and calibration-critical cargo.",
    keywords: "air suspension trailer India, air suspension vs standard trailer, machinery transport India, damage-free transport",
    body: [
      { h: "What air suspension actually does", p: [
        "A standard leaf-spring trailer transfers a lot of road shock straight into the load. An air-suspension trailer cushions that shock with pneumatic bags, keeping the deck far more stable over potholes, expansion joints and rough patches — the reality of most Indian highways.",
      ]},
      { h: "When you need it", p: [
        "Calibration-critical machinery, CNC and precision tools, electronics, medical and lab equipment, transformers and defence electro-optics all benefit from air suspension. If a jolt could knock a machine out of calibration or crack a sensitive component, air suspension pays for itself in avoided damage.",
      ]},
      { h: "When a standard trailer is fine", p: [
        "For rugged cargo — steel structures, plates, most construction machinery and general full-truck-load freight — a well-loaded standard multi-tyre trailer is perfectly safe and more economical.",
      ]},
      { h: "How we decide", p: [
        "We match the trailer to the cargo, not the other way round. Our fleet of 800+ vehicles includes air-suspension trailers alongside high-bed, low-bed and semi low-bed units, so your machinery rides on exactly what it needs.",
      ]},
    ],
  },
  {
    slug: "explosive-vans-defence-cargo-safety",
    title: "How Explosive Vans Keep Defence Cargo Safe",
    tag: "Defence",
    date: "2026-05-28",
    readTime: "5 min read",
    image: IMAGES.explosiveVan,
    excerpt:
      "Moving ammunition and explosives is nothing like moving ordinary freight. Purpose-built explosive vans and strict discipline are what keep it safe.",
    description:
      "How licensed explosive vans transport ammunition and hazardous defence stores safely in India — construction, fire-suppression, escorts and compliance for Class-1 dangerous goods.",
    keywords: "explosive van India, ammunition transport India, defence cargo transport, hazardous goods transport",
    body: [
      { h: "Why ordinary trucks won't do", p: [
        "Ammunition, explosives and hazardous defence stores are Class-1 dangerous goods. They demand purpose-built vehicles, trained crews and documentation that an ordinary freight truck simply cannot provide.",
      ]},
      { h: "What makes a van an 'explosive van'", p: [
        "A licensed explosive van is built for the job — spark-safe cargo areas, fire-suppression equipment, secure anchoring, statutory hazard placards and compliance with Indian explosives and defence transport regulations. Crews are trained specifically for this cargo.",
      ]},
      { h: "Escorts and route planning", p: [
        "These movements run escorted and route-planned, avoiding dense population where required and coordinated with the relevant authorities. Every consignment is sealed and fully documented from origin to destination.",
      ]},
      { h: "A trusted defence partner", p: [
        "ITO moves for the Indian defence ecosystem every day — ammunition in explosive vans, tanks and armoured vehicles on low-beds, and sensitive drones and electro-optics on air-suspension trailers. Safety and documentation come first, always.",
      ]},
    ],
  },
  {
    slug: "transporting-industrial-chemical-tanks",
    title: "Transporting Industrial & Chemical Storage Tanks: An ODC Guide",
    tag: "Tanks",
    date: "2026-05-20",
    readTime: "6 min read",
    image: IMAGES.industrialTank,
    excerpt:
      "Refinery vessels, chemical reactors and giant storage tanks are some of the most challenging ODC loads on the road. Here's how they move.",
    description:
      "A guide to transporting industrial and chemical storage tanks, pressure vessels and reactors as ODC cargo in India — trailers, route surveys, permits and safe handling.",
    keywords: "industrial tank transport India, chemical tank transport, pressure vessel transport India, reactor vessel transport, ODC transport India",
    body: [
      { h: "Not the tanks you might think", p: [
        "When people hear 'tanks' they picture the military kind — and yes, we move those. But a huge share of our tank movements are industrial: cylindrical storage tanks, pressure vessels, chemical reactors and refinery columns bound for process plants across India.",
      ]},
      { h: "Why they're difficult", p: [
        "These vessels are long, wide and sometimes very tall — classic over-dimensional cargo. They're also often single-piece and irreplaceable on a schedule, so damage or delay is not an option. Some carry cleanliness or coating requirements that rule out rough handling.",
      ]},
      { h: "The right trailer and the right plan", p: [
        "We move them on low-bed, semi low-bed and modular trailers sized to the exact dimensions, with saddles and cradles to spread the load and protect the shell. A route survey confirms every clearance, and permits and escorts are arranged before departure.",
      ]},
      { h: "From plant to plant, intact", p: [
        "Whether it's a chemical reactor for a process plant or a storage tank for a refinery, our job is to deliver it exactly as it left the fabricator — on time, to any pincode in India.",
      ]},
    ],
  },
];

export const postBySlug = (slug) => POSTS.find((p) => p.slug === slug);
